import { SortDirection, SortingState, VisibilityState } from '@tanstack/react-table';
import { CubeQuery } from '@/types/cube';
import { CustomColumnDef } from '@/types/table';

type SingleCubeQuery = Exclude<CubeQuery, readonly unknown[]>;

type ColumnFieldMap = Record<string, string[]>;

type DownloadOrder = [string, SortDirection][];

const isSingleQuery = (query: CubeQuery): query is SingleCubeQuery => query != null && !Array.isArray(query);

export const buildColumnFieldMapFromColumns = <T>(columns: CustomColumnDef<T>[]): ColumnFieldMap => {
  const columnFieldMap: ColumnFieldMap = {};

  for (const column of columns) {
    if (column.id != null && column.downloadDimensions?.length) {
      columnFieldMap[column.id] = column.downloadDimensions;
    }
  }

  return columnFieldMap;
};

export const applyColumnVisibilityToDownloadQuery = (
  baseQuery: CubeQuery,
  columnFieldMap: ColumnFieldMap,
  visibility: VisibilityState,
): CubeQuery => {
  if (!isSingleQuery(baseQuery)) {
    return baseQuery;
  }

  const ownedFields = new Set<string>();
  const visibleFields = new Set<string>();

  for (const [columnId, fields] of Object.entries(columnFieldMap)) {
    if (!(columnId in visibility)) {
      continue;
    }

    const isVisible = visibility[columnId] !== false;
    for (const field of fields) {
      ownedFields.add(field);
      if (isVisible) {
        visibleFields.add(field);
      }
    }
  }

  if (ownedFields.size === 0) {
    return baseQuery;
  }

  const keepField = (field: string): boolean => !ownedFields.has(field) || visibleFields.has(field);
  const { dimensions, measures } = baseQuery;

  return {
    ...baseQuery,
    ...(dimensions ? { dimensions: dimensions.filter(keepField) } : {}),
    ...(measures ? { measures: measures.filter(keepField) } : {}),
  };
};

export const applyTableSortToDownloadQuery = (
  baseQuery: CubeQuery,
  columnFieldMap: ColumnFieldMap,
  sorting: SortingState,
): CubeQuery => {
  if (!sorting.length || !isSingleQuery(baseQuery)) {
    return baseQuery;
  }

  const { dimensions, measures } = baseQuery;
  const queryFields = new Set<string>(dimensions);
  for (const measure of measures ?? []) {
    queryFields.add(measure);
  }

  const order: DownloadOrder = [];
  for (const { id, desc } of sorting) {
    const fields = columnFieldMap[id];
    if (!fields?.length) {
      continue;
    }

    const direction: SortDirection = desc ? 'desc' : 'asc';
    for (const field of fields) {
      if (queryFields.has(field)) {
        order.push([field, direction]);
      }
    }
  }

  if (!order.length) {
    return baseQuery;
  }

  return {
    ...baseQuery,
    order,
  };
};
